import { FormEvent, useState } from 'react';
import styled from 'styled-components';

import { useAgentActions, useAgentState } from '../../../providers/contexts';
import { shortAddress } from '../chat-utils';
import { Container, UrlInput } from '../chat.styles';
import { TerminalIcon } from '../icons';

/** Shown when the host has no active account (signed out). */
export function SignedOutState() {
  return (
    <Container>
      <EmptyState>
        <IconWrapper>
          <TerminalIcon size={32} color="var(--text-secondary)" />
        </IconWrapper>
        <Title>Sign in to use the terminal</Title>
        <Description>
          Connect your account to pair an AI agent for this profile.
        </Description>
      </EmptyState>
    </Container>
  );
}

/** Setup screen when no agent URL is configured for the current account */
export function AgentUrlSetup({
  accountId,
  onConnected,
}: {
  readonly accountId: string;
  readonly onConnected?: () => void;
}) {
  const agentState = useAgentState();
  const agentActions = useAgentActions();
  // Pre-fill the URL from the live agent state so a manual disconnect →
  // reconnect is a single click. The token is write-only here: `connect`
  // persists it to the package's internal token store, but it isn't read
  // back into the form, so a reconnect leaves the field blank unless the
  // user re-enters it (the stored token is reused on reconnect regardless).
  const [url, setUrl] = useState(() => agentState.agentUrl ?? '');
  const [token, setToken] = useState('');

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmedUrl = url.trim();
    if (trimmedUrl.length === 0) return;
    // `connect(url, token)` persists both through the package's internal
    // agent-url store before the socket opens: the URL is stored, and a
    // non-blank token is written to the token store (clearing any stale
    // device token) so the handshake sends it as `auth.token`.
    agentActions.connect(trimmedUrl, token.trim());
    onConnected?.();
  };

  return (
    <Container>
      <EmptyState>
        <IconWrapper>
          <TerminalIcon size={32} color="var(--text-secondary)" />
        </IconWrapper>
        <Title>Connect AI agent for {shortAddress(accountId)}</Title>
        <Description>
          Enter the WebSocket URL and gateway token of your AI agent instance.
          Each account has its own pairing.
        </Description>
        <SetupForm onSubmit={handleSubmit}>
          <UrlInput
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="ws://your-agent-ip:18789"
            autoComplete="off"
            data-1p-ignore
          />
          <UrlInput
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Gateway token"
            autoComplete="off"
            data-1p-ignore
          />
          <ConnectButton type="submit" disabled={url.trim().length === 0}>
            Connect
          </ConnectButton>
        </SetupForm>
      </EmptyState>
    </Container>
  );
}

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  /* Center when there's room, but fall back to top-aligned + scroll when the
     pane is too short (e.g. Paradigm's bottom-tab dock) so the form is never
     clipped. The parent clips (overflow: hidden); this is the scroll viewport. */
  justify-content: safe center;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  gap: 8px;
  padding: 24px;
`;

const IconWrapper = styled.div`
  opacity: 0.4;
  margin-bottom: 8px;
`;

const Title = styled.div`
  color: var(--text-primary);
  font-size: 0.875rem;
  font-weight: 500;
`;

const Description = styled.div`
  color: var(--text-secondary);
  font-size: 0.75rem;
  text-align: center;
  max-width: 280px;
  margin-bottom: 12px;
`;

const SetupForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 320px;
`;

const ConnectButton = styled.button`
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  background: var(--button-color);
  color: var(--text-primary);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    opacity: 0.85;
  }
`;
